import { bind, Component, h, signal, tag } from "omi";
import confetti from "canvas-confetti";

import { networkSignal } from "@/utils/signals";
import { DocumentRef } from "@/api";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { initApi } from "@/utils";

import { ActionNotification } from "./ui/actionNotification";
import { NWProjectShareDialogOptions, NWProjectShareDialogProjectComplete, ProjectShareView } from "./views";

type Props = {
  close: () => void;
};

@tag("nw-project-features-share")
export class NWProjectShareDialog extends Component<Props> {
  static css = [tailwind];

  private readonly ATTEMPT_LIMIT = 8;
  private readonly SHOW_NOTIFICATION_DURATION = 6000;
  private readonly POLLING_DELAY = 4000;

  private activeView = signal<ProjectShareView>(ProjectShareView.PROJECT_COMPLETE);
  private files = networkSignal<DocumentRef[] | undefined>();
  private attempts = signal(0);
  private actionNotification = signal<
    { title: string; type: "success" | "error"; isClosing?: boolean; link?: string | (() => void) } | undefined
  >(undefined);

  ready() {
    this.triggerConfetti();
  }

  @bind
  private async handleRetrieveFiles() {
    while (this.attempts.value < this.ATTEMPT_LIMIT) {
      const result = await this.retrieveFiles();
      if (result) {
        this.attempts.value = 0;
        return;
      }
      await new Promise(resolve => setTimeout(resolve, this.POLLING_DELAY));
    }
    this.attempts.value = 0;
  }

  @bind
  private async retrieveFiles(): Promise<boolean> {
    await this.files.run(async () => await this.getDocumentLinks());
    const hasFiles = this.files.signal.data.value != null && this.files.signal.data.value.some(f => f.type === "pdf");
    if (!hasFiles) {
      this.attempts.value += 1;
    }
    return hasFiles;
  }

  @bind
  private async getDocumentLinks(): Promise<DocumentRef[] | undefined> {
    const projectId = Store.project.id.value;

    if (!projectId) {
      return undefined;
    }

    const docs = await initApi().getProjectDocRefs({ projectId });
    const pdfDoc = docs.find(d => d.type === "pdf" && d._public === false);

    if (pdfDoc) {
      return docs;
    }

    return undefined;
  }

  @bind
  private handleViewChange(view: ProjectShareView) {
    this.activeView.value = view;
  }

  @bind
  private handleShowActionNotification(
    type: "success" | "error",
    title: string,
    timeMs = this.SHOW_NOTIFICATION_DURATION,
    link?: string | (() => void),
  ) {
    this.actionNotification.value = { type, title, link };

    setTimeout(() => {
      if (this.actionNotification.value) {
        this.actionNotification.value = {
          ...this.actionNotification.value,
          isClosing: true,
        };
      }
    }, timeMs);

    setTimeout(() => {
      this.actionNotification.value = undefined;
    }, timeMs + 1000);
  }

  private triggerConfetti() {
    confetti({
      particleCount: 400,
      spread: 100,
      zIndex: 999,
      gravity: 2,
      angle: 45,
      startVelocity: 100,
      origin: {
        x: 0,
        y: 1,
      },
      drift: 1,
      ticks: 400,
    });

    confetti({
      particleCount: 400,
      spread: 100,
      zIndex: 999,
      gravity: 2,
      angle: 135,
      startVelocity: 100,
      origin: {
        x: 1,
        y: 1,
      },
      drift: -1,
      ticks: 400,
    });
  }

  render(props: Props) {
    const projectId = Store.project.id.value;

    if (!projectId) {
      return null;
    }

    const activeIndex = this.activeView.value === ProjectShareView.PROJECT_COMPLETE ? 0 : 1;

    return (
      <>
        <ActionNotification data={this.actionNotification.value} />
        <div class="bg-white rounded-xl text max-w-md w-full max-h-3/4 z-[100]">
          <div class="content relative overflow-hidden">
            <div
              class="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${100 * activeIndex}%)` }}
            >
              <div class="w-full flex-shrink-0">
                <NWProjectShareDialogProjectComplete
                  close={props.close}
                  onNext={() => this.handleViewChange(ProjectShareView.SHARE_OPTIONS)}
                />
              </div>
              <div class="w-full flex-shrink-0">
                <NWProjectShareDialogOptions
                  retrieveFiles={this.handleRetrieveFiles}
                  close={props.close}
                  onBack={() => this.handleViewChange(ProjectShareView.PROJECT_COMPLETE)}
                  showActionNotification={this.handleShowActionNotification}
                  data={{ projectId, files: this.files.signal.data.value }}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}
